import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { fetchWsToken, wsUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const WsContext = createContext(null);

export function WsProvider({ children }) {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const listenersRef = useRef(new Set());

  const connect = useCallback(async () => {
    if (!user) return;
    try {
      const { token } = await fetchWsToken();
      if (!token) return;
      const ws = new WebSocket(wsUrl(token));
      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        wsRef.current = null;
        // Reconnect after 3s
        setTimeout(() => user && connect(), 3000);
      };
      ws.onerror = () => setConnected(false);
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          listenersRef.current.forEach((cb) => cb(data));
        } catch {}
      };
      wsRef.current = ws;
    } catch {}
  }, [user]);

  useEffect(() => {
    if (user) connect();
    return () => {
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [user, connect]);

  const subscribe = useCallback((cb) => {
    listenersRef.current.add(cb);
    return () => listenersRef.current.delete(cb);
  }, []);

  return (
    <WsContext.Provider value={{ connected, subscribe, wsRef }}>
      {children}
    </WsContext.Provider>
  );
}

export const useWs = () => useContext(WsContext) || { connected: false, subscribe: () => () => {}, wsRef: { current: null } };
